import { Box, Divider, Grid2, Skeleton, styled, tooltipClasses, Typography } from "@mui/material";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import InfoIcon from "../../Components/Icons/InfoIcon";
import { useEffect, useState } from "react";
import { getDiagnosticsStats } from "../../Api/FleetOverviewApi";
import { tooltipLabels } from "./AlertConfigs";
import { usePagesContent } from "../../Hooks/usePagesContent";

const defaultFetchStatsFn = (globalFilters) => getDiagnosticsStats(globalFilters, false, 'bmsFaults');

// Define severity-based color theme
const severityStyles = {
  critical: {
    bg: "#FFF3F3",
    zeroBg: "#FFFFFF",
    border: "#B50500",
    text: "#B50500",
    actionText: "need action immediately",
  },
  'high severity': {
    bg: "#FFFFFF",
    zeroBg: "#FFFFFF",
    border: "#FF6B0F",
    text: "#FF6B0F",
    actionText: "need action",
  },
  warning: {
    bg: "#FFFFFF",
    zeroBg: "#FFFFFF",
    border: "#DCA101",
    text: "#DCA101",
    actionText: "need action",
  }
};

const StyledSeverityBoxContainer = styled(Box)(() => {
  return {
    border: "1px solid #E6E6E6",
    borderRadius: "9px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  };
});

const StyledSeverityBox = styled(Box)(({ severity, count }) => {
  const style = severityStyles[severity] || severityStyles.warning;
  return {
    backgroundColor: count === 0 ? style.zeroBg : style.bg,
    marginLeft: '-1px',
    borderLeft: `3px solid ${style.border}`,
    padding: "16px 24px",
    borderRadius: "9px",
    display: "flex",
    flexDirection: "column",
    height: '100%',
  };
});

const isValidNumber = (val) => Number.isInteger(val);

const SeverityStatistics = ({
  globalFilters,
  applyFiltersFromStats,
  defaultFiltersApplied,
  globalFiltersLoaded,
  autoApplyFirstFilter = false,
  fetchStatsFn = defaultFetchStatsFn,
  overdueConfigKey = 'alerts.BMSv2.config.overdueNumDays',
}) => {

  const { metaInfo } = usePagesContent();
  const overdueNumDays = metaInfo?.globalConfig?.[overdueConfigKey] ?? null;
  let overdueTooltipText = overdueNumDays != null ? `Triggered more than ${overdueNumDays} days ago` : null;
  if (overdueNumDays === 1) {
    overdueTooltipText = `Triggered more than 1 day ago`;
  }

  const [alertStats, setAlertStats] = useState([]);
  const [loading, setLoading] = useState(false);

  const processStats = (statsData) => {
    if (!statsData || typeof statsData !== "object") return [];

    const { activeStats } = statsData;

    // Convert active severityBreakup
    const activeList = (activeStats?.severityBreakup || []).map((item) => ({
      severityName: item.severityName,
      severity: item.severityName?.toLowerCase() || "",
      label: item.severityName?.toUpperCase() || "",
      ...(isValidNumber(item.total) && { totalCount: item.total }),
      ...(isValidNumber(item.totalToday) && { todayCount: item.totalToday }),
      ...(isValidNumber(item.overdueCount) && { overdueCount: item.overdueCount }),
      ...(isValidNumber(item.totalBatteriesAffected) && {
        affectedCount: item.totalBatteriesAffected,
      }),
    }));

    return activeList;
  }

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const statsResponse = await fetchStatsFn(globalFilters);
      const statsData = statsResponse?.response?.data || {};
      const processedData = processStats(statsData);
      setAlertStats(processedData);
      setLoading(false);
    };

    // Only fetch when all global filters are loaded AND default filters are applied
    if (Object.keys(globalFilters).length > 0 && defaultFiltersApplied && globalFiltersLoaded) {
      fetchStats();
    }
  }, [globalFilters, defaultFiltersApplied, globalFiltersLoaded, fetchStatsFn]);

  useEffect(() => {
    if (!autoApplyFirstFilter) return;
    for (const alertStat of alertStats) {
      if (alertStat?.severityName && alertStat?.totalCount > 0) {
        applyFiltersFromStats({severityName: [alertStat.severityName]});
        break;
      }
    }
  }, [alertStats, autoApplyFirstFilter])

  if (loading) {
    return (
      <Grid2 container gap={2}>
        {[0, 1, 2].map((i) => (
          <StyledSeverityBoxContainer key={i}>
            <Box sx={{ padding: "16px 24px", display: "flex", flexDirection: "column", height: "100%" }}>
              <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
              <Grid2 container gap={1.5} mb={1.5} alignItems="flex-end">
                <Skeleton variant="text" width={48} height={40} />
                <Grid2 container gap={1.5}>
                  <Skeleton variant="text" width={60} height={20} />
                  <Skeleton variant="text" width={80} height={20} />
                </Grid2>
              </Grid2>
              <Skeleton variant="text" width={120} height={20} />
            </Box>
          </StyledSeverityBoxContainer>
        ))}
      </Grid2>
    );
  }

  return (
    <Grid2 container gap={2}>
      {alertStats.map((alert, index) => {
        const style = severityStyles[alert.severity];
        return (
          <StyledSeverityBoxContainer key={index}>
            <StyledSeverityBox severity={alert.severity} count={alert.totalCount}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography
                  variant="fw600s14c000000"
                  sx={{ color: "#2E2E2E", lineHeight: "20px" }}
                >
                  {alert.label}
                </Typography>
                <PrimaryTooltip
                  title={tooltipLabels.severity?.[alert.severity]}
                  placement="right"
                  sx={{
                    [`& .${tooltipClasses.tooltip}`]: {
                      maxWidth: "150px",
                      textAlign: "left !important",
                    }
                  }}
                >
                  <InfoIcon sx={{ width: 14, height: 14, "&:hover ": { color: "#4D44D8" }}} />
                </PrimaryTooltip>
              </Box>
              <Grid2 container gap={1.5} mb={1.5} alignItems={'flex-end'}>
                  <Box>
                      <Typography
                        variant="fw500s20c000000"
                        sx={{color: style.text,fontSize: "32px",lineHeight: "32px"}}
                      >
                        {alert.totalCount}
                      </Typography>
                  </Box>
                  <Grid2 container gap={1.5}>
                      {
                        isValidNumber(alert.todayCount) && (
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Typography variant="fw400s14c8F8F8F" sx={{color: '#6D6D6D', lineHeight: '20px'}}>Today:</Typography>
                            <Typography variant="fw500s14c000000" sx={{lineHeight: '20px'}}>{alert.todayCount}</Typography>
                          </Box>
                        )
                      }
                      {
                        overdueNumDays != null && isValidNumber(alert.overdueCount) && (
                          <>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4" }} />
                              <PrimaryTooltip title={overdueTooltipText} placement="bottom">
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                  <Typography variant="fw400s14c8F8F8F" sx={{color: '#6D6D6D', lineHeight: '20px'}}>Overdue:</Typography>
                                  <Typography variant="fw500s14c000000" sx={{lineHeight: '20px'}}>{alert.overdueCount}</Typography>
                                </Box>
                              </PrimaryTooltip>
                          </>
                        )
                      }
                  </Grid2>
              </Grid2>
              <Box>
                <Typography variant="fw500s14c494949" sx={{lineHeight: '20px'}}>
                  {alert.affectedCount}{" "}
                  {alert.affectedCount === 1 ? "Vehicle" : "Vehicles"}{" Affected"}
                </Typography>
              </Box>
            </StyledSeverityBox>
          </StyledSeverityBoxContainer>
        );
      })}
    </Grid2>
  );
};

export default SeverityStatistics;
